/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { ReactNode } from "react";
import { When } from "react-if";
import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { EventActionAttempt, EventActionScope } from "~/generated/graphql";
import { getLastItem } from "~/tools";
import { useEvent } from "~/views/events/EventsDetails/EventProvider";
import { EventEntity } from "~/views/events/types";
import { ActionLog } from "./ActionLog";
import { Rerun } from "./Rerun";

type Props = {
  eventEntity: EventEntity;
  attempt?: EventActionAttempt;
  children?: ReactNode;
};

export function EventResourceHeader({ eventEntity, attempt, children }: Props) {
  const { event } = useEvent();
  const { id: eventId } = event;

  const lastEventActionAttemptId =
    eventEntity?.attempts && getLastItem(eventEntity.attempts).id;

  const outputUri =
    attempt?.state?.outputUri ?? eventEntity?.globalState?.outputUri;
  const modalLabel = attempt?.attempt
    ? `Attempt ${attempt?.attempt.toString()}`
    : eventEntity?.action?.name;

  const eventStatus = eventEntity.globalState?.status;

  return (
    <>
      <Box>
        <FlexContainer justifyContent="flex-end" gap={theme.spacing.spacing02}>
          <When condition={Boolean(children)}>
            <>{children}</>
          </When>
          <When condition={eventEntity?.scope === EventActionScope.Plan}>
            <ActionLog
              outputUri={outputUri ?? ""}
              modalLabel={modalLabel ?? ""}
            />
          </When>
          <When condition={lastEventActionAttemptId}>
            <Rerun
              {...{ eventId }}
              eventActionId={lastEventActionAttemptId}
              eventStatus={eventStatus}
            />
          </When>
        </FlexContainer>
      </Box>
      <Spacer height={theme.spacing.spacing04} />
    </>
  );
}
